/**
 * 格式化工具函數
 * 統一處理各種數據格式化需求
 */

/**
 * 千分位格式化
 * @param {number|string} value - 要格式化的數值
 * @returns {string} 格式化後的字符串
 */
export function formatCurrency(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '';
  }
  
  // 轉換為萬元單位並加上千分位符號
  return (numValue / 10000)
    .toString()
    .replace(/^(-?\d+?)((?:\d{3})+)(?=\.\d+$|$)/, function(all, pre, groupOf3Digital) {
      return pre + groupOf3Digital.replace(/\d{3}/g, ',$&');
    });
}

/**
 * 資本額格式化 (顯示為億/萬)
 * @param {number|string} value - 要格式化的數值
 * @returns {string} 格式化後的字符串
 */
export function formatCapital(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }  
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '';
  }
  
  if (numValue >= 100000000) {
    return (numValue / 100000000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ' 億';
  } else if (numValue >= 10000) {
    return (numValue / 10000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ' 萬';
  } else {
    return (numValue / 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ' 仟';
  }
}

/**
 * 日期格式化 (YYYMMDD -> YYY/MM/DD)
 * @param {string} value - 要格式化的日期字符串
 * @returns {string} 格式化後的日期字符串
 */
export function formatDate(value) {
  if (!value || value.length !== 7) {
    return '';
  }
  
  return value.slice(0, 3) + '/' + value.slice(3, 5) + '/' + value.slice(5);
}

/**
 * 數字格式化 (通用千分位)
 * @param {number|string} value - 要格式化的數值
 * @param {number} decimals - 小數位數，默認為0
 * @returns {string} 格式化後的字符串
 */
export function formatNumber(value, decimals = 0) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '';
  }
  
  return numValue.toLocaleString('zh-TW', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

/**
 * 地址簡化格式化 (提取區域部分)
 * @param {string} address - 完整地址
 * @returns {string} 簡化後的地址
 */
export function formatAddressBrief(address) {
  if (!address) return '';
  
  // 可能是"區", "鄉"
  const districtPattern = /區|鄉/;
  const idxPos = address.search(districtPattern);
  if (idxPos === -1) {
    // 未找到區或鄉，直接截取前6個字符
    return address.slice(0, 6);
  }

  return address.slice(0, idxPos + 1);
}

/**
 * 字符串簡化格式化
 * @param {string} str - 要簡化的字符串
 * @param {number} maxLength - 最大長度，默認為20
 * @returns {string} 簡化後的字符串
 */
export function formatBriefing(str, maxLength = 20) {
  if (!str) return '';
  
  if (str.length > maxLength) {
    return str.substr(0, maxLength) + " ...";
  } else {
    return str;
  }
}

/**
 * 行業代碼分割
 * @param {string} industryCodeString - 行業代碼字符串
 * @returns {Array} 分割後的行業代碼數組
 */
export function splitIndustryCode(industryCodeString) {
  if (!industryCodeString) return [];
  
  return industryCodeString.match(/\d{6} [^\d]+(?=\d|$)/g) || [];
}

/**
 * 提取6位數字行業代碼
 * @param {string} text - 包含行業代碼的文本
 * @returns {string} 提取的6位數字代碼
 */
export function extractIndustryCode(text) {
  if (!text) return '';
  
  const regex = /(\D|^)(\d{6})(\D|$)/g;
  const match = regex.exec(text);
  
  if (match !== null) {
    return match[2];
  } else {
    return text;
  }
}

/**
 * 處理空值或無效值
 * @param {any} value - 要檢查的值
 * @param {string} defaultValue - 默認值，默認為空字符串
 * @returns {string} 處理後的值
 */
export function handleNullValue(value, defaultValue = '') {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  return value;
}

/**
 * 電話號碼格式化
 * @param {string} phone - 電話號碼
 * @returns {string} 格式化後的電話號碼
 */
export function formatPhone(phone) {
  if (!phone) return '';
  
  // 移除所有非數字字符
  const cleaned = phone.replace(/\D/g, '');
  
  // 根據長度進行格式化
  if (cleaned.length === 10) {
    // 手機號碼格式: 0912-345-678
    return cleaned.replace(/(\d{4})(\d{3})(\d{3})/, '$1-$2-$3');
  } else if (cleaned.length >= 8) {
    // 市話格式: 02-1234-5678
    return cleaned.replace(/(\d{2,3})(\d{4})(\d{4})/, '$1-$2-$3');
  }
  
  return phone;
}

/**
 * 百分比格式化
 * @param {number} value - 數值
 * @param {number} decimals - 小數位數，默認為1
 * @returns {string} 百分比字符串
 */
export function formatPercentage(value, decimals = 1) {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  
  return (value * 100).toFixed(decimals) + '%';
}

/**
 * 統一編號格式化
 * @param {string} taxId - 統一編號
 * @returns {string} 格式化後的統一編號
 */
export function formatTaxId(taxId) {
  if (!taxId) return '';
  
  const cleaned = taxId.replace(/\D/g, '');
  if (cleaned.length === 8) {
    return cleaned.replace(/(\d{4})(\d{4})/, '$1-$2');
  }
  
  return taxId;
}

// 默認導出所有格式化函數
export default {
  formatCurrency,
  formatCapital,
  formatDate,
  formatNumber,
  formatAddressBrief,
  formatBriefing,
  splitIndustryCode,
  extractIndustryCode,
  handleNullValue,
  formatPhone,
  formatPercentage,
  formatTaxId
};